'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from "@/utils/supabase/client";
import { removeBookFromList, saveBook2 } from '@/app/actions/bookActions';
import { useIsClient } from '@/hooks/useBrowser';
import "../BookInteractionPill/book-interaction-pill.css"
import { BookInterface } from '../../books/types/book';
import { ChevronRight } from 'lucide-react';
import { saveAllBooks } from '@/app/actions/bookActions';
import { getCollectionsForDropdown } from '@/app/actions/listActions';
import CollectionsDropdown from './CollectionsDropdown';

interface CollectionDropdownItem {
  id: string;
  name: string;
  isDefault: boolean;
  bookCount: number;
}

interface CollectionsDropdownResult {
  lists?: CollectionDropdownItem[];
  error?: string;
}
interface SaveButtonProps {
  work_id: string;
  isSaved: boolean;
  onSave: (workId: string) => void;
  onUnsave?: (workId: string) => void; // Optional prop for handling unsave
  openPopout: string | null;
  setOpenPopout: React.Dispatch<React.SetStateAction<string | null>>;
  allBooks?: BookInterface[]; // All books in the current search results
  query?: string; // Current search query
  collectionId?: string; // Collection ID for when viewing a specific collection
}

export default function SaveButton({ work_id, isSaved, onSave, onUnsave, openPopout, setOpenPopout,   allBooks = [],
  query = "", collectionId}: SaveButtonProps) {
    const [message, setMessage] = useState('')
    const router = useRouter()
    const supabase = createClient()
    const isClient = useIsClient();
    const [isSearchPage, setIsSearchPage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isCollectionPage, setIsCollectionPage] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showCollections, setShowCollections] = useState(false);
    const [collections, setCollections] = useState<any[]>([]);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [savedCollections, setSavedCollections] = useState<string[]>([]);
    const [isRemoved, setIsRemoved] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [isSavingToQuery, setIsSavingToQuery] = useState(false); // For save to query
    const [isSavingAllToQuery, setIsSavingAllToQuery] = useState(false); // For save all to query
    const [hasAddedToQuery, setHasAddedToQuery] = useState(false);
    const [hasAddedAllToQuery, setHasAddedAllToQuery] = useState(false);
    useEffect(() => {
      if (!isClient) return;
        const checkAuthStatus = async () => {
        const { data, error } = await supabase.auth.getSession();
        const isAuth = !!data.session && !error;
        setIsAuthenticated(isAuth);
        
        if (isAuth) {
          loadCollections();
        }
      };
      checkAuthStatus();
      
      const currentUrl = window.location;
      const path = currentUrl.pathname;
      setIsSearchPage(path.includes('/discover'));
      setIsCollectionPage(path.includes('/profile/collections/'));
      
    }, [isClient]); 


    useEffect(() => {
      setHasAddedToQuery(false);
      setIsSavingToQuery(false);
      setSavedCollections([]);
      
      if (openPopout === "save") {
        setOpenPopout(null);
      }
      setShowCollections(false);
    }, [work_id]); // Just keep work_id as the dependency

    const redirectToLogin = () => {
      if (!isClient) return;
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      router.push(`/sign-up?returnUrl=${returnUrl}&saveBook=${work_id}`);
    };

    const handleSubmit = async () => {
      if (!isClient) return;
      if (isSaved) {
        if (onUnsave) {
          onUnsave(work_id);
        }
        const result = await removeBookFromList(work_id);
        if (result.error) {
          setMessage(result.error);
        } else {
          
          setMessage('Book Removed');
        }
      } else {
        onSave(work_id);
        const result = await saveBook2(work_id);
        if (result.error) {
          setMessage(result.error);
        } else if (result.success) {
          setMessage('Book Saved');
        }
      }
    }
 
    const loadCollections = async () => {
      setLoadingCollections(true);
      try {
        const result = await getCollectionsForDropdown();
        
        if (result.error) {
          console.error('Error fetching collections:', result.error);
          return;
        }
        console.log(result)
        if (result.lists && result.lists.length > 0) {
          const nonDefaultLists = result.lists.filter((list: CollectionDropdownItem) => !list.isDefault);
          setCollections(nonDefaultLists);
        }
      } catch (error) {
        console.error('Error loading collections:', error);
      } finally {
        setLoadingCollections(false);
      }
    };

    const handleSaveAllToQueryList = async (): Promise<void> => {
      if (!isClient || !allBooks || allBooks.length === 0) return;
      setIsSaving(true);
      setMessage('');

      try {
        const listName = query 
        const workIds: string[] = allBooks.map(book => book.work_urn);

        const result = await saveAllBooks(workIds, listName);
        if (result.success) {
          setHasAddedAllToQuery(true);

          if (result.savedCount === 0 && result.skippedCount && result.skippedCount > 0) {
            setMessage(`All books were already in "${listName}" list`);
          } else if (result.skippedCount && result.skippedCount > 0) {
            setMessage(`Saved ${result.savedCount} books to "${listName}" list. ${result.skippedCount} were already saved.`);
          } else {
            setMessage(`Saved all ${result.savedCount} books to "${listName}" list`);
          }
          if (!isSaved && workIds.includes(work_id)) {
          }
        } else {
          console.error('Failed to save all books:', result.error);
          setMessage(`Error: ${result.error}`);
        }
      } catch (error) {
        console.error('Error saving all books:', error);
        setMessage('An unexpected error occurred');
      } finally {
        setIsSaving(false);
      }
    }
    const handleSaveToCollection = async (collectionId: string, collectionName: string) => {
      if (!isClient) return;
      setMessage('');
      
      try {
        const result = await saveBook2(work_id, collectionName);
        
        if (result.error) {
          setMessage(result.error);
        } else if (result.success) {
          setSavedCollections(prev => {
            if (!prev.includes(collectionId)) {
              return [...prev, collectionId];
            }
            return prev;
          });
          setMessage(`Saved to "${collectionName}"`);
        }
      } catch (error) {
        console.error('Error saving to collection:', error);
        setMessage('Error saving book');
      } finally {
        setIsSaving(false);
      }
    };
    const toggleCollections = () => {
      if (!loadingCollections) {
        setShowCollections(!showCollections);
      }
    };

    const handleSaveToQueryList = async () => {
      if (!isClient || !query) return;
      setIsSavingToQuery(true);

      try {
        const result = await saveBook2(work_id, query);
        if (result.error) {
          setMessage(result.error);
        } else if (result.success) {
          setHasAddedToQuery(true);
          setMessage(`Book saved to "${query}"`);
          setIsSavingToQuery(false);
        }
      } catch (error) {
        console.error('Error saving book:', error);
        setMessage('Error saving book');
      } finally {
        setIsSaving(false);
      }
    }

    const handleRemoveFromCollection = async () => {
      if (!isClient || !collectionId) return;
      setIsRemoving(true);
      
      try {
        const result = await removeBookFromList(work_id, collectionId);
        console.log(result)
        if (result.error) {
          setMessage(result.error);
        } else {
          setMessage(`Removed from collection`);
          setIsRemoved(true);
          if (onUnsave && isCollectionPage) {
            onUnsave(work_id);
          }
        }
      } catch (error) {
        console.error('Error removing book from collection:', error);
        setMessage('Error removing book');
      } finally {
        setIsRemoving(false);
      }
    };

    const handleClick = () => {
          if (!isAuthenticated) {
            redirectToLogin();
            return;
          }
          if (openPopout === "save") {
            setOpenPopout(null); 
            setShowCollections(false);

          } else {
            setOpenPopout("save");
          }
        };

    return (
      <div className="share-button relative save-pop">
        {openPopout === "save" && isAuthenticated && (
          <div className="">
            <>
            <button
                onClick={handleSubmit} 
                disabled={isSaving}
                className=""
                title={`Save book to default list`}>
              {isSaved? "Saved" : "Save"}
              </button>
            </>
            <button 
              onClick={toggleCollections}
              className=""
              disabled={loadingCollections}
            >
            <span>
                {loadingCollections ? "Loading collections..." : "Add to collection"}
              </span>
              {!loadingCollections && (
                <ChevronRight 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    transition: 'transform 0.2s',
                    transform: showCollections ? 'rotate(90deg)' : 'none' 
                  }}
                />
              )}
            </button>
            <CollectionsDropdown
                workId={work_id}
                isVisible={showCollections}
                onSaveToCollection={handleSaveToCollection}
                savedCollections={savedCollections}
                isSaving={isSaving}
                loadingCollections={loadingCollections}
                collections={collections}
                loadCollections={loadCollections}
              />
            {isCollectionPage && (
              <button
                onClick={handleRemoveFromCollection}
                disabled={isRemoving}
                className=""
                                title={`Remove book from collection`}
              >
                {isRemoving ? "Removing..." : isRemoved ? "Removed" : "Remove book from this collection"}
              </button>
            )}
            {isSearchPage && (
           <>
           <button 
             onClick={handleSaveToQueryList} 
             disabled={isSavingToQuery || hasAddedToQuery}
             className="collection-button"
             title={`Save book to "${query}" list`}
           >
            {isSavingToQuery 
                  ? "Saving..." 
                  : hasAddedToQuery 
                    ? `Added to "${query.length > 15 ? query.substring(0, 15) + '...' : query}" list` 
                    : `Save book to "${query.length > 15 ? query.substring(0, 15) + '...' : query}" list`
                }
           </button> 
           {allBooks && allBooks.length > 0 && (
             <button 
               onClick={handleSaveAllToQueryList} 
               disabled={isSavingAllToQuery || hasAddedAllToQuery}
               className="collection-button"
               title={`Save all ${allBooks.length} books to "${query}" list`}
             >
              {isSavingAllToQuery 
                  ? "Saving..." 
                  : hasAddedAllToQuery 
                    ? `Added all books to "${query.length > 15 ? query.substring(0, 15) + '...' : query}" list` 
                    : `Save all ${allBooks.length} books to "${query.length > 15 ? query.substring(0, 15) + '...' : query}" list`
                }
             </button>
           )}
         </>
            )}
          </div>
        )}
        <button 
          onClick={handleClick}
          aria-label={isSaved ? "Saved" : "Save"}
          className={"action-button"}
        >
          {isCollectionPage? "Manage" : "Save"}
        </button>
      </div>
    );
}